import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  styled,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../common/button/commonButton';

const LogoutContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '90vh',
  padding: theme.spacing(3),
}));

const LogoutCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  borderRadius: 12,
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
  overflow: 'visible',
  position: 'relative',
}));

const LogoutIconContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -30,
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: "black",
  borderRadius: '50%',
  width: 60,
  height: 60,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
}));

const LogoutPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('initial');

  useEffect(() => {
    const logoutAction = new URLSearchParams(window.location.search).get('action');
    if (logoutAction === 'process') {
      handleLogout();
    }
  }, []);

  const clearAllStorage = () => {
    const itemsToRemove = ['authToken', 'user', 'permissions'];
    itemsToRemove.forEach(item => localStorage.removeItem(item));
        localStorage.clear();
    sessionStorage.clear();

  };

  const handleLogout = async () => {
    setStatus('loading');
    
    try {
      clearAllStorage();
      await new Promise(resolve => setTimeout(resolve, 800));
      setStatus('success');
      
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error) {
      console.error('Logout error:', error);
      setStatus('error');
    }
  };

  const handleCancel = () => {
    navigate(-1); 
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <CircularProgress size={40} color="primary" sx={{ mb: 3 }}/>
            <Typography variant="h6" align="center" gutterBottom>
              Logging you out...
            </Typography>
            <Typography variant="body2" align="center" color="textSecondary">
              Please wait while we securely end your session.
            </Typography>
          </>
        );
        
      case 'success':
        return (
          <>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60, mb: 2 }}/>
            <Typography variant="h6" align="center" gutterBottom>
              Successfully Logged Out
            </Typography>
            <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
              You have been securely logged out of your account.
            </Typography>
            <CustomButton 
              type="button"
              text="Login"
              variant="contained"
              fullWidth
              disableElevation
              onClick={() => navigate('/')}
            />
          </>
        );
        
      case 'error':
        return (
          <>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }}/>
            <Typography variant="h6" align="center" gutterBottom>
              Logout Error
            </Typography>
            <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
              There was a problem logging you out. Please try again.
            </Typography>
            <CustomButton 
              type="button"
              text="Try Again"
              variant="contained"
              fullWidth
              disableElevation
              onClick={handleLogout}
            />
          </>
        );
        
      default:
        return (
          <>
            <Typography variant="h6" align="center" gutterBottom>
              Are you sure you want to log out?
            </Typography>
            <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
              You will need to login again to access the system.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <CustomButton 
                type="button"
                text="Cancel"
                variant="contained"
                disableElevation
                size="small"
                onClick={handleCancel}
              />
              <CustomButton 
                type="submit"
                text="Logout"
                variant="contained"
                disableElevation
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
              />
            </Box>
          </>
        );
    }
  };

  return (
    <LogoutContainer>
      <LogoutCard>
        <LogoutIconContainer>
          <LogoutIcon fontSize="large" />
        </LogoutIconContainer>
        <CardContent sx={{ pt: 6, px: 4, pb: 4, textAlign: 'center' }}>
          {renderContent()}
        </CardContent>
      </LogoutCard>
    </LogoutContainer>
  );
};

export default LogoutPage;